import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { getApiErrorMessage } from "@/lib/api";
import { Shield, RefreshCw, User, Settings as SettingsIcon, FileText, Users, KeyRound } from "lucide-react";

const ACTION_META = {
  "user.create": { label: "User created", Icon: User, color: "#22D3C0" },
  "user.deactivate": { label: "User deactivated", Icon: User, color: "#F87171" },
  "user.reactivate": { label: "User reactivated", Icon: User, color: "#22D3C0" },
  "user.reset_password": { label: "Password reset", Icon: KeyRound, color: "#F4B860" },
  "settings.update": { label: "Settings updated", Icon: SettingsIcon, color: "#F4B860" },
  "settings.stripe_override": { label: "Stripe keys changed", Icon: SettingsIcon, color: "#FF6A00" },
  "lead.batch_assign": { label: "Bulk lead assign", Icon: Users, color: "#8AECFF" },
};

function formatRelative(iso) {
  if (!iso) return "";
  try {
    const t = new Date(iso).getTime();
    const s = Math.floor((Date.now() - t) / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch (e) {
    return "";
  }
}

/**
 * Admin audit log panel — surfaces recent admin actions (user changes, settings,
 * Stripe key overrides, bulk lead assignments). Shown on the Settings page for
 * admin + office_manager roles.
 */
export function AuditLogPanel() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/admin/audit-log", { params: { limit: 50 } });
      setRows(data?.rows || []);
      setTotal(data?.total || 0);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <section
      className="mt-6 border border-line rounded-sm bg-bg-surface"
      data-testid="audit-log-panel"
    >
      <div className="px-5 py-3 border-b border-line flex items-center gap-2">
        <Shield size={14} className="text-cyan" />
        <h2 className="text-base font-medium text-ink">Audit log</h2>
        <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-ink-muted">
          {total} total
        </span>
        <button
          onClick={load}
          disabled={refreshing}
          data-testid="audit-log-refresh"
          className="ml-auto flex items-center gap-1 text-xs border border-line hover:bg-bg-hover text-ink-secondary hover:text-ink px-2 py-1 transition-colors disabled:opacity-40"
        >
          <RefreshCw size={11} className={refreshing ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="px-5 py-4 space-y-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-10 bg-bg animate-pulse rounded-sm" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-ink-muted">
          No admin actions logged yet.
        </div>
      ) : (
        <div className="divide-y divide-line max-h-[500px] overflow-y-auto">
          {rows.map((r) => {
            const meta = ACTION_META[r.action] || { label: r.action, Icon: FileText, color: "#70827D" };
            const { Icon } = meta;
            return (
              <div key={r.id} className="px-5 py-3 flex items-start gap-3 text-sm" data-testid={`audit-row-${r.id}`}>
                <div
                  className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5"
                  style={{ background: `${meta.color}22`, border: `1px solid ${meta.color}55` }}
                >
                  <Icon size={11} style={{ color: meta.color }} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span
                      className="text-[10px] font-mono uppercase tracking-widest"
                      style={{ color: meta.color }}
                    >
                      {meta.label}
                    </span>
                    <span className="text-[10px] text-ink-muted font-mono">
                      · {r.actor_name} ({r.actor_role})
                    </span>
                    <span className="text-[10px] text-ink-muted ml-auto font-mono whitespace-nowrap">
                      {formatRelative(r.timestamp)}
                    </span>
                  </div>
                  <div className="mt-0.5 text-ink text-[13px] leading-tight break-words">
                    {r.summary}
                  </div>
                  {r.ip && (
                    <div className="mt-0.5 text-[10px] text-ink-muted font-mono">
                      IP {r.ip}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
